import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
} from 'lexical'
import type { LexicalCommand, LexicalEditor } from 'lexical'
import { useEffect, useRef, useState } from 'react'
import * as React from 'react'

import { $createImageNode, $isImageNode, ImageNode } from '../nodes/image-node'
import type { ImagePayload } from '../nodes/image-node'
import { DialogActions, DialogButtonsList } from '../ui/dialog'
import { FileText, Link as LinkIcon } from 'lucide-react'
import UsersLatestUploads from '@/components/user-latest-update'
import { Button } from '@/components/ui/button'

export const CAN_USE_DOM: boolean =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.document.createElement !== 'undefined'

const getDOMSelection = (targetWindow: Window | null): Selection | null =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null

export const INSERT_IMAGE_COMMAND: LexicalCommand<ImagePayload> = createCommand('INSERT_IMAGE_COMMAND')

export function InsertImageUriDialogBody({ onClick }: { onClick: (payload: { altText: string; src: string }) => void }): React.JSX.Element {
  const [src, setSrc] = useState('')
  const [altText, setAltText] = useState('')

  const isDisabled = src === ''

  return (
    <>
      <input
        placeholder='i.e. https://source.unsplash.com/random'
        onChange={(e) => setSrc(e.target.value)}
        value={src}
        data-test-id='image-modal-url-input'
        className='focus:border-blue-500 mb-2 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 outline-none'
      />
      <input
        placeholder='Descriptive alternative text'
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
        data-test-id='image-modal-alt-text-input'
        className='focus:border-blue-500 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 outline-none'
      />

      <DialogActions>
        <Button
          disabled={isDisabled}
          size="sm"
          className='ml-auto'
          onClick={() => onClick({ altText, src })}
        >
          Confirm
        </Button>
      </DialogActions>
    </>
  )
}

export function InsertImageUploadedDialogBody({ onClick }: { onClick: (payload: { altText: string; src: string | File }) => void }): React.JSX.Element {
  const [src, setSrc] = useState<string | File>('')
  const [altText, setAltText] = useState('')

  const isDisabled = src === ''

  const loadImage = (files: FileList | null) => {
    if (files !== null && files.length > 0) {
      const file = files[0]
      setSrc(
        new File([file], file.name.replace(/[^a-zA-Z0-9_.]/g, '').replace(/[\s-]/g, ''), {
          type: file.type
        })
      )
    }
  }

  return (
    <>
      <UsersLatestUploads
        onClick={(upload: any) => onClick({ altText: upload?.image_path, src: upload?.image_path })}
      />
      <p className='text-sm mb-2 font-medium text-foreground'>Upload new image</p>
      <div className='mb-2'>
        <input
          type='file'
          accept='image/*'
          className='text-gray-600 text-sm mt-1 file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer'
          onChange={(e) => loadImage(e.target.files)}
          data-test-id='image-modal-file-upload'
        />
      </div>
      <input
        placeholder='Descriptive alternative text'
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
        data-test-id='image-modal-alt-text-input'
        className='focus:border-blue-500 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 outline-none'
      />

      <DialogActions>
        <Button
          disabled={isDisabled}
          size="sm"
          className='ml-auto'
          onClick={() => onClick({ altText, src })}
        >
          Confirm
        </Button>
      </DialogActions>
    </>
  )
}

/**
 * InsertImageDialog
 *
 * NOTE: image uploading is now pluggable — pass `uploadFile` prop (file => Promise<src string>)
 * from the application (e.g., project page) to handle server upload. Plugins should not
 * call the addPublicImageResource mutation directly.
 */
export function InsertImageDialog({ editor, onClose, uploadFile }: { editor: LexicalEditor; onClose: () => void; uploadFile?: (file: File) => Promise<string> }): React.JSX.Element {
  const [mode, setMode] = useState<'url' | 'file' | null>(null)
  const hasModifier = useRef(false)

  useEffect(() => {
    hasModifier.current = false
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey
    }
    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('keydown', handler)
    }
  }, [editor])


  const onClick = async (payload: { altText: string; src: string | File }) => {
    if (typeof payload.src === 'string') {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, payload as ImagePayload)
      onClose()
      return
    }

    try {
      if (!uploadFile) {
        console.error('InsertImageDialog: uploadFile handler is not provided.')
        onClose()
        return
      }
      const uploadedSrc = await uploadFile(payload.src as File)
      if (uploadedSrc) {
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          src: uploadedSrc,
          altText: payload.altText || uploadedSrc
        })
      }
      onClose()
    } catch (error) {
      console.error('Failed to upload image via uploadFile handler:', error)
    }
  }

  return (
    <>
      {!mode && (
        <DialogButtonsList>
          <button
            className='bg-white border border-blue-500 hover:bg-blue-50 p-4 rounded-lg w-full flex flex-col justify-center items-center text-blue-600 transition-colors gap-2'
            onClick={() => setMode('file')}
            aria-label='File'
          >
            <FileText className='h-8 w-8' />
            <span className='font-semibold'>File</span>
          </button>
          <button
            className='bg-white border border-blue-500 hover:bg-blue-50 p-4 rounded-lg w-full flex flex-col justify-center items-center text-blue-600 transition-colors gap-2'
            onClick={() => setMode('url')}
            aria-label='URL'
          >
            <LinkIcon className='h-8 w-8' />
            <span className='font-semibold'>URL</span>
          </button>
        </DialogButtonsList>
      )}
      {mode === 'url' && <InsertImageUriDialogBody onClick={onClick} />}
      {mode === 'file' && <InsertImageUploadedDialogBody onClick={onClick} />}
    </>
  )
}

export default function ImagesPlugin({ captionsEnabled }: { captionsEnabled?: boolean }): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor')
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_IMAGE_COMMAND,
        (payload: ImagePayload) => {
          const imageNode = $createImageNode(payload)
          $insertNodes([imageNode])
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd()
          }

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event)
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event)
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor)
        },
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [captionsEnabled, editor])

  return null
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
const img = CAN_USE_DOM ? document.createElement('img') : {} as HTMLImageElement
if (CAN_USE_DOM) {
  (img as HTMLImageElement).src = TRANSPARENT_IMAGE
}

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection()
  if (!node) {
    return false
  }
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) {
    return false
  }
  dataTransfer.setData('text/plain', '_')
  dataTransfer.setDragImage(img as HTMLImageElement, 0, 0)
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width
      },
      type: 'image'
    })
  )

  return true
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection()
  if (!node) {
    return false
  }
  if (!canDropImage(event)) {
    event.preventDefault()
  }
  return true
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection()
  if (!node) {
    return false
  }
  const data = getDragImageData(event)
  if (!data) {
    return false
  }
  event.preventDefault()
  if (canDropImage(event)) {
    const range = getDragSelection(event)
    node.remove()
    const rangeSelection = $createRangeSelection()
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range)
    }
    $setSelection(rangeSelection)
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data)
  }
  return true
}

function $getImageNodeInSelection(): ImageNode | null {
  const selection = $getSelection()
  if (!$isNodeSelection(selection)) {
    return null
  }
  const nodes = selection.getNodes()
  const node = nodes[0]
  return $isImageNode(node) ? node : null
}

function getDragImageData(event: DragEvent): ImagePayload | null {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag')
  if (!dragData) {
    return null
  }
  const { type, data } = JSON.parse(dragData)
  if (type !== 'image') {
    return null
  }

  return data
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-image') &&
    target.parentElement &&
    target.parentElement.closest('div.ContentEditable__root')
  )
}

function getDragSelection(event: DragEvent): Range | null {
  let range: Range | null = null
  const target = event.target as Node
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
        ? (target as Document).defaultView
        : target.ownerDocument?.defaultView
  const domSelection = getDOMSelection(targetWindow || null)
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY)
  } else if ((event as any).rangeParent && domSelection !== null) {
    domSelection.collapse((event as any).rangeParent, (event as any).rangeOffset || 0)
    range = domSelection.getRangeAt(0)
  } else {
    throw Error(`Cannot get the selection when dragging`)
  }

  return range
}
